import type { DeviceInterfaces, DeviceType, DiagramNode, PortLayout } from './types.js';
import type { DevicePort, PortSide } from './devicePorts.js';
import { getPortProfile } from './portProfiles.js';

function tAlongSide(side: PortSide, idxOnSide: number, totalOnSide: number): number {
  if (totalOnSide <= 1) return 0.5;
  const sequential = idxOnSide / (totalOnSide + 1);
  if (side === 'right' || side === 'top') return 1 - sequential;
  return sequential;
}

function assignCopperSides(count: number, layout: PortLayout): PortSide[] {
  if (count <= 0) return [];
  if (layout === 'left') return Array.from({ length: count }, () => 'left' as const);
  if (layout === 'right') return Array.from({ length: count }, () => 'right' as const);
  if (layout === 'both') {
    const left = Math.ceil(count / 2);
    return [
      ...Array.from({ length: left }, () => 'left' as const),
      ...Array.from({ length: count - left }, () => 'right' as const),
    ];
  }
  const order: PortSide[] = ['left', 'bottom', 'right', 'top'];
  const base = Math.floor(count / 4);
  const rem = count % 4;
  const out: PortSide[] = [];
  for (let i = 0; i < 4; i++) {
    const n = base + (i < rem ? 1 : 0);
    for (let j = 0; j < n; j++) out.push(order[i]!);
  }
  return out;
}

/** Defaults from common device roles (overridden by live/TTX toggles). */
export function defaultDeviceInterfaces(type: DeviceType): DeviceInterfaces {
  switch (type) {
    case 'router':
    case 'wifi_router':
      return {
        hasWan: true,
        hasEthernet: true,
        ethernetCount: 4,
        hasSfp: false,
        sfpCount: 0,
        hasWifi: type === 'wifi_router',
        wifiCount: type === 'wifi_router' ? 2 : 0,
        wifi24: type === 'wifi_router',
        wifi58: false,
      };
    case 'access_point':
      return {
        hasWan: false,
        hasEthernet: true,
        ethernetCount: 1,
        hasSfp: false,
        sfpCount: 0,
        hasWifi: true,
        wifiCount: 2,
        wifi24: true,
        wifi58: true,
      };
    case 'lte_antenna':
      return {
        hasWan: false,
        hasEthernet: true,
        ethernetCount: 1,
        hasSfp: false,
        sfpCount: 0,
        hasWifi: false,
        wifiCount: 0,
        wifi24: false,
        wifi58: false,
      };
    case 'firewall':
      return {
        hasWan: true,
        hasEthernet: true,
        ethernetCount: 2,
        hasSfp: false,
        sfpCount: 0,
        hasWifi: false,
        wifiCount: 0,
        wifi24: false,
        wifi58: false,
      };
    case 'switch':
      return {
        hasWan: false,
        hasEthernet: true,
        ethernetCount: 8,
        hasSfp: false,
        sfpCount: 0,
        hasWifi: false,
        wifiCount: 0,
        wifi24: false,
        wifi58: false,
      };
    case 'modem':
      return {
        hasWan: true,
        hasEthernet: true,
        ethernetCount: 1,
        hasSfp: false,
        sfpCount: 0,
        hasWifi: false,
        wifiCount: 0,
        wifi24: false,
        wifi58: false,
      };
    case 'wifi_phone':
    case 'tablet':
      return {
        hasWan: false,
        hasEthernet: false,
        ethernetCount: 0,
        hasSfp: false,
        sfpCount: 0,
        hasWifi: true,
        wifiCount: 1,
        wifi24: true,
        wifi58: true,
      };
    default:
      return {
        hasWan: false,
        hasEthernet: true,
        ethernetCount: 1,
        hasSfp: false,
        sfpCount: 0,
        hasWifi: false,
        wifiCount: 0,
        wifi24: false,
        wifi58: false,
      };
  }
}

export function resolveDeviceInterfaces(
  node: Pick<DiagramNode, 'type' | 'interfaces' | 'portCount' | 'wifiAntennaCount' | 'portProfileId'>,
): DeviceInterfaces {
  const base = defaultDeviceInterfaces(node.type);
  const raw = node.interfaces;
  if (!raw) {
    const eth = typeof node.portCount === 'number' ? node.portCount : base.ethernetCount ?? 0;
    const wifi =
      typeof node.wifiAntennaCount === 'number' ? node.wifiAntennaCount : base.wifiCount ?? 0;
    return {
      ...base,
      hasEthernet: eth > 0,
      ethernetCount: Math.max(0, eth - (base.hasWan ? 1 : 0)),
      hasWan: base.hasWan && eth > 0,
      hasWifi: wifi > 0,
      wifiCount: wifi,
      wifi24: wifi > 0 ? base.wifi24 ?? true : false,
      wifi58: wifi > 0 ? base.wifi58 ?? false : false,
    };
  }
  return {
    hasWan: Boolean(raw.hasWan),
    hasEthernet: Boolean(raw.hasEthernet),
    ethernetCount: Math.min(64, Math.max(0, Math.round(raw.ethernetCount ?? 0))),
    hasSfp: Boolean(raw.hasSfp),
    sfpCount: Math.min(4, Math.max(0, Math.round(raw.sfpCount ?? 0))),
    hasWifi: Boolean(raw.hasWifi),
    wifiCount: Math.min(16, Math.max(0, Math.round(raw.wifiCount ?? 0))),
    wifi24: Boolean(raw.wifi24),
    wifi58: Boolean(raw.wifi58),
  };
}

export function interfacesWiredCount(iface: DeviceInterfaces): number {
  const wan = iface.hasWan ? 1 : 0;
  const eth = iface.hasEthernet ? Math.max(0, iface.ethernetCount ?? 0) : 0;
  const sfp = iface.hasSfp ? Math.max(0, iface.sfpCount ?? 0) : 0;
  return wan + eth + sfp;
}

export function buildPortsFromInterfaces(
  iface: DeviceInterfaces,
  layout: PortLayout = 'both',
): DevicePort[] {
  type Spec = { id: string; label: string; role: 'wan' | 'lan' | 'eth' | 'sfp'; side?: PortSide };
  const specs: Spec[] = [];

  if (iface.hasWan) {
    specs.push({ id: 'WAN', label: 'WAN', role: 'wan', side: layout === 'right' ? 'right' : 'left' });
  }

  const ethN = iface.hasEthernet ? Math.max(0, iface.ethernetCount ?? 0) : 0;
  for (let i = 1; i <= ethN; i++) {
    specs.push({
      id: iface.hasWan ? `LAN-${i}` : `Eth-${i}`,
      label: iface.hasWan ? `LAN-${i}` : `Eth-${i}`,
      role: iface.hasWan ? 'lan' : 'eth',
    });
  }

  const sfpN = iface.hasSfp ? Math.min(4, Math.max(0, iface.sfpCount ?? 0)) : 0;
  for (let i = 1; i <= sfpN; i++) {
    specs.push({
      id: `SFP-${i}`,
      label: sfpN === 1 ? 'SFP' : `SFP-${i}`,
      role: 'sfp',
      side: 'top',
    });
  }

  const copper = specs.filter((s) => s.role !== 'sfp' && !s.side);
  const copperSides = assignCopperSides(copper.length, layout);
  let ci = 0;
  const sides: PortSide[] = specs.map((s) => {
    if (s.side) return s.side;
    if (s.role === 'sfp') return 'top';
    return copperSides[ci++] ?? 'left';
  });

  const perSide = { left: 0, right: 0, top: 0, bottom: 0 };
  const sideTotals = { left: 0, right: 0, top: 0, bottom: 0 };
  for (const s of sides) sideTotals[s] += 1;

  return specs.map((spec, i) => {
    const side = sides[i]!;
    perSide[side] += 1;
    return {
      id: spec.id,
      label: spec.label,
      index: i + 1,
      side,
      t: tAlongSide(side, perSide[side], sideTotals[side]),
      kind: 'ethernet' as const,
      role: spec.role,
    };
  });
}

export function summarizeInterfacesUk(iface: DeviceInterfaces): string {
  const bits: string[] = [];
  if (iface.hasWan) bits.push('1 WAN');
  if (iface.hasEthernet && (iface.ethernetCount ?? 0) > 0) {
    bits.push(`${iface.ethernetCount} ${iface.hasWan ? 'LAN' : 'Eth'}`);
  }
  if (iface.hasSfp && (iface.sfpCount ?? 0) > 0) bits.push(`${iface.sfpCount} SFP`);
  if (iface.hasWifi && (iface.wifiCount ?? 0) > 0) {
    const bands = [iface.wifi24 ? '2.4' : null, iface.wifi58 ? '5.8' : null].filter(Boolean);
    bits.push(`${iface.wifiCount} Wi‑Fi${bands.length ? ` (${bands.join('+')} ГГц)` : ''}`);
  }
  return bits.join(' · ') || 'немає інтерфейсів';
}

/** Toggle state for the edit form (interfaces / profile / legacy counts). */
export function interfacesForEditor(
  node: Pick<
    DiagramNode,
    'type' | 'interfaces' | 'portCount' | 'wifiAntennaCount' | 'portProfileId'
  >,
): DeviceInterfaces {
  if (node.interfaces) return resolveDeviceInterfaces(node);

  const profile = getPortProfile(node.portProfileId);
  const wifi =
    typeof node.wifiAntennaCount === 'number'
      ? node.wifiAntennaCount
      : defaultDeviceInterfaces(node.type).wifiCount ?? 0;

  if (profile) {
    const wan = profile.uplink === 'sfp' ? 0 : profile.wan;
    const copperLan = profile.lan + profile.eth;
    return {
      hasWan: wan > 0,
      hasEthernet: copperLan > 0,
      ethernetCount: copperLan,
      hasSfp: profile.sfp > 0,
      sfpCount: Math.min(4, profile.sfp),
      hasWifi: wifi > 0,
      wifiCount: wifi,
      wifi24: wifi > 0,
      wifi58: false,
    };
  }

  return resolveDeviceInterfaces(node);
}
